<script setup>
  import { ref, watch, computed } from "vue";
  import { storeToRefs } from "pinia";

  import { FormDialog, FormField } from "@/components/episto-ui/form-dialog";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Textarea } from "@/components/ui/textarea";
  import { DateTimePicker } from "@/components/episto-ui/date-time-picker";

  // Convert the DateTimePicker's ISO string back to a YYYY-MM-DD using local
  // time getters (not slice(0,10) on the ISO, which would shift by one day for
  // users east of UTC because toISOString() converts to UTC).
  const toDateOnly = (isoStr) => {
    if (!isoStr) return "";
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };

  import { useContractsStore } from "@stores/contracts";
  import { useAccountsStore } from "@stores/accounts";

  const props = defineProps({
    open: { type: Boolean, default: false },
    contract: { type: Object, default: null },
  });

  const emit = defineEmits(["update:open", "saved"]);

  const accountsStore = useAccountsStore();
  const contractsStore = useContractsStore();
  const { accounts } = storeToRefs(accountsStore);

  const isEditing = computed(() => Boolean(props.contract?.id));

  const form = ref({
    name: "",
    starts_at: "",
    ends_at: "",
    total_credits: 0,
    deal_id: null,
    comment: "",
    account_ids: [],
  });

  const loading = ref(false);
  const error = ref(null);

  const accountOptions = computed(() =>
    (accounts.value || []).map((a) => ({ label: a.name, value: a.id })),
  );

  const canSave = computed(
    () =>
      form.value.name.trim() &&
      form.value.starts_at &&
      form.value.ends_at &&
      form.value.total_credits > 0 &&
      form.value.account_ids.length > 0,
  );

  watch(
    () => [props.open, props.contract],
    ([open, contract]) => {
      if (!open) return;
      form.value = {
        name: contract?.name || "",
        starts_at: contract?.starts_at || "",
        ends_at: contract?.ends_at || "",
        total_credits: contract?.total_credits || 0,
        deal_id: contract?.deal_id ?? null,
        comment: contract?.comment || "",
        account_ids: contract?.accounts?.map((a) => a.id) || [],
      };
      error.value = null;
      if (!accounts.value.length) accountsStore.fetch();
    },
    { immediate: true },
  );

  const save = async () => {
    loading.value = true;
    error.value = null;
    try {
      if (isEditing.value) {
        await contractsStore.updateContract(props.contract.id, form.value);
      } else {
        await contractsStore.createContract(form.value);
      }
      emit("saved");
      emit("update:open", false);
    } catch (e) {
      error.value = e?.message || "An error occurred while saving the contract.";
    } finally {
      loading.value = false;
    }
  };
</script>

<template>
  <FormDialog
    :open="open"
    :is-editing="isEditing"
    entity-name="Contract"
    :loading="loading"
    :can-save="canSave"
    max-width="500px"
    @update:open="emit('update:open', $event)"
    @save="save">
    <FormField
      id="contract-name"
      label="Contract Name">
      <Input
        id="contract-name"
        v-model="form.name"
        placeholder="e.g. Contrat Annuel 2026" />
    </FormField>

    <FormField
      id="contract-accounts"
      label="Accounts">
      <Select
        v-model="form.account_ids"
        :options="accountOptions"
        :multiple="true"
        :filterable="true"
        placeholder="Select accounts..." />
    </FormField>

    <div class="grid grid-cols-2 gap-4">
      <FormField
        id="contract-starts-at"
        label="Start Date">
        <DateTimePicker
          :model-value="form.starts_at"
          placeholder="Pick a start date"
          @update:model-value="form.starts_at = toDateOnly($event)" />
      </FormField>

      <FormField
        id="contract-ends-at"
        label="End Date">
        <DateTimePicker
          :model-value="form.ends_at"
          placeholder="Pick an end date"
          @update:model-value="form.ends_at = toDateOnly($event)" />
      </FormField>
    </div>

    <FormField
      id="contract-total-credits"
      label="Total Credits">
      <Input
        id="contract-total-credits"
        v-model.number="form.total_credits"
        type="number"
        min="1"
        placeholder="e.g. 20" />
    </FormField>

    <FormField
      id="contract-deal-id"
      label="HubSpot Deal ID">
      <Input
        id="contract-deal-id"
        v-model.number="form.deal_id"
        type="number"
        min="1"
        placeholder="e.g. 12345678901" />
    </FormField>

    <FormField
      id="contract-comment"
      label="Comment (internal)">
      <Textarea
        id="contract-comment"
        v-model="form.comment"
        :rows="3"
        placeholder="License context, renewal notes… visible to Episto staff only" />
    </FormField>

    <p
      v-if="error"
      class="text-sm text-status-error-strong">
      {{ error }}
    </p>
  </FormDialog>
</template>
